import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { X, Loader2, Download } from "lucide-react";
import ExportDateRangePicker from "./ExportDateRangePicker";
import toast from "react-hot-toast";

/**
 * props:
 *  - open, onClose
 *  - cities                -> { [cityId]: cityName }            (already in Workorderdashboard)
 *  - hubsList               -> [{ id, hubName, cityId }]          (getAllHubsListData)
 *  - workorderTypeValues    -> { [typeId]: typeName }
 *  - workorderStatusValue   -> { [statusId]: statusName }
 *  - apiUrl, token          -> for the vendor lookup + final download call
 */
const WEAR_AND_TEAR_SUBTYPES = [
  "Clutch",
  "Tyres",
  "Battery",
  "Wind Shield",
  "Wheel Alignment / Balancing",
];

const ExportWorkOrderModal = ({
  open,
  onClose,
  cities,
  hubsList,
  workorderTypeValues,
  workorderStatusValue,
  workorderType, // needed for the .inverted.wearAndTear check, same as the dashboard's filter bar
  apiUrl,
  token,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  oneMonthAgo.setHours(0, 0, 0, 0);

  const [selectionRange, setSelectionRange] = useState({
    startDate: oneMonthAgo,
    endDate: today,
    key: "selection",
  });

  const [selectedCity, setSelectedCity] = useState("");
  const [selectedHub, setSelectedHub] = useState("");

  // Vendor list isn't sourced anywhere in Workorderdashboard yet, so it's
  // fetched here the same way hubs are fetched there. Swap the endpoint
  // below for the real one if it differs.
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState("");

  const [selectedType, setSelectedType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedSubType, setSelectedSubType] = useState("");

  const isWearAndTear =
    selectedType && selectedType === workorderType?.inverted?.wearAndTear;

  useEffect(() => {
    if (!open) return;
    const getAllVendorsList = async () => {
      setVendorsLoading(true);
      try {
        const response = await fetch(`${apiUrl}/api/vendors?limit=1000`);
        const data = await response.json();
        if (data?.statusCode === "0100") {
          setVendors(data?.entities?.rows || []);
        }
      } catch (error) {
        console.error("Error fetching Vendor Names:", error);
      } finally {
        setVendorsLoading(false);
      }
    };
    getAllVendorsList();
  }, [open, apiUrl]);

  // Reset the whole form whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    setConfirmOpen(false);
    setSelectionRange({
      startDate: oneMonthAgo,
      endDate: today,
      key: "selection",
    });
    setSelectedCity("");
    setSelectedHub("");
    setSelectedVendor("");
    setSelectedType("");
    setSelectedStatus("");
    setSelectedSubType("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // clear subType if the user switches away from Wear And Tear
  useEffect(() => {
    if (!isWearAndTear) setSelectedSubType("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  const hubsForSelectedCity = selectedCity
    ? (hubsList || []).filter((hub) => String(hub?.cityId) === String(selectedCity))
    : hubsList || [];

  const isFormComplete = Boolean(
    selectionRange?.startDate && selectionRange?.endDate,
  );

  // "YYYY-MM-DDTHH:mm" in local time — matches the API's expected format,
  // no seconds/millis/timezone suffix.
  const formatLocalDateTime = (date, endOfDay) => {
    const d = new Date(date);
    if (endOfDay) d.setHours(23, 59, 0, 0);
    else d.setHours(0, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  };

    const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({
        startDate: formatLocalDateTime(selectionRange.startDate, false),
        endDate: formatLocalDateTime(selectionRange.endDate, true),
      });

      if (selectedCity) params.set("cityId", selectedCity);
      if (selectedHub) params.set("hubId", selectedHub);
      if (selectedVendor) params.set("vendorId", selectedVendor);
      if (selectedType) params.set("serviceType", selectedType);
      if (selectedStatus) params.set("serviceStatus", selectedStatus);

      if (isWearAndTear && selectedSubType) {
        params.set("subType", selectedSubType);
      }

      // Needs the /api prefix — without it the request hits the SPA's
      // static fallback instead of the real backend (no CORS support there).
      const url = `${apiUrl}/api/vehicleservices/report-download?${params.toString()}`;

      // No custom headers here on purpose — Authorization/Content-Type both
      // trigger a CORS preflight (OPTIONS), and this endpoint's OPTIONS
      // handler appears to hang/not exist, causing the request to time out.
      // A plain GET with no auth needed (confirmed via Postman) stays a
      // "simple request" and skips preflight entirely.
      const response = await toast.promise(
        fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `${token}`,
          },
        }),
        {
          loading: "Generating report...",
          success: "Report generated successfully!",
          error: "Failed to generate report!",
        },
      );

      const data = await response.json();

      // Note: this endpoint returns Location under dataValues, not entities
      // (different shape from the vehicle-issues report endpoint).
      if (data?.statusCode === "0100" && data?.dataValues?.Location) {
        setConfirmOpen(false);
        onClose();
        window.open(data.dataValues.Location, "_blank");
      } else {
        toast.error(data?.dataValues?.msg || "Failed to generate report!", {
          position: "top-center",
          style: {
            background: "white",
            color: "red",
            fontWeight: "medium",
            marginBottom: "20px",
          },
        });
      }
    } catch (error) {
      console.error("Error downloading work order report:", error);
      toast.error("Failed to generate report!");
    } finally {
      setDownloading(false);
    }
  };

  const selectClass =
    "h-11 w-full rounded-lg border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
          },
          "& .MuiDialog-paper": {
            marginTop: "64px",
          },
        }}
      >
        <DialogContent className="!p-0">
          <div className="flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Export Work Orders</h2>
              <div className="flex items-center gap-2">
                <div
                  title='Data is searched with respect to the date state of "created on"'
                  className="relative group"
                >
                  <div className="w-5 h-5 rounded-full bg-gray-700 text-white text-xs flex items-center justify-center cursor-default select-none">
                    i
                  </div>
                  <div className="pointer-events-none absolute right-0 top-6 w-56 rounded-md bg-gray-900 text-white text-xs px-2 py-1.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50">
                    Data is searched with respect to the date state of "created on"
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded-full hover:bg-gray-100"
                  type="button"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Body — two rows of three fields each */}
            <div className="px-6 py-6 flex flex-col gap-5">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Date Range
                  </label>
                  <ExportDateRangePicker
                    selectionRange={selectionRange}
                    setSelectionRange={setSelectionRange}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    City
                  </label>
                  <select
                    className={selectClass}
                    value={selectedCity}
                    onChange={(e) => {
                      setSelectedCity(e.target.value);
                      setSelectedHub("");
                    }}
                  >
                    <option value="">Select City</option>
                    {Object.entries(cities || {}).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Hub
                  </label>
                  <select
                    className={selectClass}
                    value={selectedHub}
                    onChange={(e) => setSelectedHub(e.target.value)}
                    disabled={!selectedCity}
                  >
                    <option value="">
                      {selectedCity ? "Select Hub" : "Select a city first"}
                    </option>
                    {hubsForSelectedCity.map((hub) => (
                      <option key={hub.id} value={hub.id}>
                        {hub.hubName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Vendor
                  </label>
                  <select
                    className={selectClass}
                    value={selectedVendor}
                    onChange={(e) => setSelectedVendor(e.target.value)}
                    disabled={vendorsLoading}
                  >
                    <option value="">
                      {vendorsLoading ? "Loading vendors..." : "Select Vendor"}
                    </option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name || vendor.vendorName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Service Type
                  </label>
                  <select
                    className={selectClass}
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    <option value="">Select Type</option>
                    {Object.entries(workorderTypeValues || {}).map(
                      ([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ),
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Service Status
                  </label>
                  <select
                    className={selectClass}
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <option value="">Select Status</option>
                    {Object.entries(workorderStatusValue || {}).map(
                      ([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              </div>

              {isWearAndTear && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Sub Type
                    </label>
                    <select
                      className={selectClass}
                      value={selectedSubType}
                      onChange={(e) => setSelectedSubType(e.target.value)}
                    >
                      <option value="">Select Sub-Type</option>
                      {WEAR_AND_TEAR_SUBTYPES.map((subType) => (
                        <option key={subType} value={subType}>
                          {subType}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 h-10 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={!isFormComplete}
                className="px-4 h-10 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700"
              >
                Download
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Small confirmation popup, overlaid on top of the export modal */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1500 }}
      >
        <DialogContent>
          <div className="flex flex-col items-center justify-center text-center gap-3 py-4">
            <Download className="w-10 h-10 text-green-500" />
            <p className="text-base font-medium">
              Your file is ready for download.
            </p>
            <p className="text-sm text-gray-500">
              Are you sure you want to download?
            </p>
            <div className="flex gap-3 mt-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="px-4 h-10 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="px-4 h-10 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700 flex items-center gap-2 disabled:opacity-60"
              >
                {downloading && <Loader2 className="w-4 h-4 animate-spin" />}
                Proceed
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExportWorkOrderModal;